import { Button, Checkbox } from "@datapay-by-datacom/design-system";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import type {
  MatchingImportWorkflowResult,
  MatchingRow,
} from "../types/matching";
import {
  MATCHING_CLUSTER_CONFIGS,
  clusterMatchingRows,
  countMatchingTabs,
  filterByTab,
  filterMatchingGroupsBySearch,
  firstMatchingClusterIdForTab,
  formatMatchingBlockedBanner,
  getVisibleMatchingGroups,
  groupMatchingRowsByClusters,
  type ClusteredMatchingRow,
  type MatchingClusterId,
  type MatchingReportTab,
  type MatchingRowStatus,
} from "../utils/matchingImportClustering";
import styles from "./MatchingImportReport.module.css";

/** Grouped by issue type (default) vs a single flat table — mirrors Pay
 * Component Import's "By component" / "All rows" preview toggle. */
export type MatchingReportLayoutMode = "byIssue" | "flat";

export interface MatchingImportReportProps {
  rows: MatchingRow[];
  result: MatchingImportWorkflowResult | null;
  phase: "idle" | "validated" | "committed";
  validationStale: boolean;
  dismissedWarningIds: ReadonlySet<string>;
  onDismissWarning: (warningId: string) => void;
  /** 0-based row indexes edited since the last validation (mirrors Pay
   * Component Import's `editedRowIndexes`) — rows in this set surface in
   * their own "Edited" cluster/tab until the batch is re-validated. */
  editedIndices?: ReadonlySet<number>;
  /** Called when the user presses a row's Edit button. Omit (or leave
   * undefined) to hide the sticky Edit column entirely — e.g. post-commit,
   * where editing is no longer offered. */
  onEditRow?: (index: number) => void;
  /** Disables every row's Edit button (e.g. while validating/committing). */
  editDisabled?: boolean;
  /** Disables Dismiss on warning rows (same busy gate as Edit). */
  dismissDisabled?: boolean;
  /**
   * Client CSV parse failure (stubs + Fail chips). Banner copy tells the user
   * to fix the file rather than implying a partial import is available.
   */
  parseFailure?: boolean;
  /**
   * CSV `fileRow` values that failed client parse. Only these stubs are
   * non-selectable during a parse-failure batch — API validation errors on
   * parsable rows stay bulk-editable.
   */
  parseFailureFileRows?: ReadonlySet<number>;
  /** 0-based selected row indexes for bulk MatchAction (Product-PRDs #400). */
  selectedIndices?: ReadonlySet<number>;
  /** Toggle a single selectable row. Omit to hide the selection column. */
  onToggleRow?: (index: number) => void;
  /**
   * Select or clear the given visible indexes (header checkbox).
   * `selected` true adds them; false removes them.
   */
  onSetVisibleSelection?: (indices: readonly number[], selected: boolean) => void;
  /** Disables selection checkboxes (e.g. while validating/committing). */
  selectionDisabled?: boolean;
}

const ALL_TABS: MatchingReportTab[] = ["all", "errors", "warnings", "edited", "passed"];

const TAB_LABELS: Record<MatchingReportTab, string> = {
  all: "All",
  errors: "Errors",
  warnings: "Warnings",
  edited: "Edited",
  passed: "Passed",
};

/** Checks-column badge label for Matching's richer (non-binary) status set. */
function checkBadgeLabel(status: MatchingRowStatus): string {
  switch (status) {
    case "error":
      return "Fail";
    case "warning":
      return "Warning";
    case "edited":
      return "Edited";
    case "imported":
      return "Imported";
    case "ready":
      return "Ready";
    case "passed":
      return "Pass";
    default:
      return "Pending";
  }
}

/**
 * Clustered validation report — issue-type sections like Work Patterns / Pay Components.
 * Pay run is bound in the UI; the table does not show a PayRunId column.
 */
export function MatchingImportReport({
  rows,
  result,
  phase,
  validationStale,
  dismissedWarningIds,
  onDismissWarning,
  editedIndices,
  onEditRow,
  editDisabled = false,
  dismissDisabled = false,
  parseFailure = false,
  parseFailureFileRows,
  selectedIndices,
  onToggleRow,
  onSetVisibleSelection,
  selectionDisabled = false,
}: MatchingImportReportProps) {
  const [tab, setTab] = useState<MatchingReportTab>("all");
  const [search, setSearch] = useState("");
  const [layoutMode, setLayoutMode] =
    useState<MatchingReportLayoutMode>("byIssue");
  const [openClusters, setOpenClusters] = useState<
    ReadonlySet<MatchingClusterId>
  >(new Set());
  const prevEditedCountRef = useRef(0);

  const showEditColumn = onEditRow !== undefined;
  const showSelectColumn = onToggleRow !== undefined;
  const selected = selectedIndices ?? new Set<number>();
  const parseStubFileRows = useMemo(
    () => parseFailureFileRows ?? new Set<number>(),
    [parseFailureFileRows]
  );

  /** Parse stubs are not bulk-editable; API error rows on parsable lines are. */
  const isRowSelectable = useCallback(
    (item: ClusteredMatchingRow) =>
      !(parseFailure && parseStubFileRows.has(item.row.fileRow)),
    [parseFailure, parseStubFileRows]
  );

  // MATCH-ALIGNED / MATCH-NO-OVERRIDE must stay visible after commit even if
  // the user dismissed them pre-commit — otherwise clustering labels the row
  // "Imported" despite no write (Alex follow-up / AC-23).
  const SKIP_ON_COMMIT_CODES = useMemo(
    () => new Set(["MATCH-ALIGNED", "MATCH-NO-OVERRIDE"]),
    []
  );
  const activeWarnings = useMemo(
    () =>
      (result?.warnings ?? []).filter((w) => {
        const id = `${w.code}:${w.indices.join(",")}`;
        if (phase === "committed" && SKIP_ON_COMMIT_CODES.has(w.code)) {
          return true;
        }
        return !dismissedWarningIds.has(id);
      }),
    [SKIP_ON_COMMIT_CODES, dismissedWarningIds, phase, result?.warnings]
  );

  const clustered = useMemo(
    () =>
      clusterMatchingRows(
        rows,
        result?.errors ?? [],
        activeWarnings,
        phase,
        validationStale,
        editedIndices
      ),
    [activeWarnings, editedIndices, phase, result?.errors, rows, validationStale]
  );

  const allGroups = useMemo(
    () => groupMatchingRowsByClusters(clustered),
    [clustered]
  );

  const visibleGroups = useMemo(
    () => getVisibleMatchingGroups(tab, allGroups),
    [allGroups, tab]
  );

  const filteredGroups = useMemo(
    () => filterMatchingGroupsBySearch(visibleGroups, search),
    [search, visibleGroups]
  );

  /** Flat layout: one row per CSV line (no multi-error fan-out), same tab + search. */
  const filteredFlatRows = useMemo(() => {
    const tabRows = filterByTab(clustered, tab);
    const wrapped = filterMatchingGroupsBySearch(
      [
        {
          clusterId: "passed",
          config: MATCHING_CLUSTER_CONFIGS.passed,
          rows: tabRows,
        },
      ],
      search
    );
    return wrapped[0]?.rows ?? [];
  }, [clustered, search, tab]);

  const counts = useMemo(() => countMatchingTabs(clustered), [clustered]);
  const editedCount = editedIndices?.size ?? 0;

  // Open the first cluster when a fresh validation/commit result lands.
  // Tab changes are handled in `handleTabChange` so we don't collapse
  // clusters the user expanded while reviewing.
  useEffect(() => {
    if (result === null) return;
    const first = firstMatchingClusterIdForTab(tab, allGroups);
    setOpenClusters(first ? new Set([first]) : new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on new result
  }, [result]);

  // After a row save, keep the Edited cluster open (and switch to All if the
  // current tab would hide it) — mirrors Pay Component Import's ReviewScreen.
  useEffect(() => {
    if (editedCount > prevEditedCountRef.current) {
      setOpenClusters((prev) => new Set([...prev, "edited"]));
      setTab((current) =>
        current === "errors" ||
        current === "warnings" ||
        current === "passed"
          ? "all"
          : current
      );
    }
    prevEditedCountRef.current = editedCount;
  }, [editedCount]);

  // Pre-commit summary banner — blocked / warnings-only / all-pass, mirrors
  // Work Patterns' banner above the tabs. Post-commit also surfaces
  // "Import results: X of Y succeeded." here (Work Patterns pattern) in
  // addition to the sticky footer status slot.
  const banner = useMemo(() => {
    if (result === null) return null;
    if (phase === "committed") {
      return {
        text: `Import results: ${result.succeeded} of ${result.total} succeeded.`,
        kind: (result.failed > 0 ? "warn" : "success") as "warn" | "success",
      };
    }
    if (validationStale) return null;
    const total = counts.all;
    if (total === 0) return null;
    if (counts.errors > 0) {
      if (parseFailure && total - counts.errors === 0) {
        const totalLabel = total === 1 ? "row" : "rows";
        return {
          text: `${counts.errors} of ${total} ${totalLabel} could not be parsed. Fix the file and upload again.`,
          kind: "error" as const,
        };
      }
      // Partial parse (some rows OK) uses the same partial-import banner as
      // validate failures so Confirm import (N of M) stays coherent.
      return {
        text: formatMatchingBlockedBanner(
          counts.errors,
          total,
          total - counts.errors
        ),
        kind: "error" as const,
      };
    }
    if (activeWarnings.length > 0) {
      return {
        text: `${counts.warnings} warning${counts.warnings === 1 ? "" : "s"} · ${counts.passed} passed · 0 failed. Check each warning before confirming import.`,
        kind: "warn" as const,
      };
    }
    return {
      text: `${total} of ${total} row${total === 1 ? "" : "s"} passed.`,
      kind: "success" as const,
    };
  }, [
    activeWarnings.length,
    counts,
    parseFailure,
    phase,
    result,
    validationStale,
  ]);

  // The Edited tab only appears once there is at least one edited row —
  // mirrors Pay Component Import's `visibleReviewTabs` ("edited" hidden when
  // tabCounts.edited === 0) — and if the user is currently on it when it
  // empties out (e.g. after a successful re-validate), fall back to "all".
  const visibleTabs = useMemo(
    () => ALL_TABS.filter((t) => t !== "edited" || counts.edited > 0),
    [counts.edited]
  );
  useEffect(() => {
    if (tab === "edited" && counts.edited === 0) {
      setTab("all");
    }
  }, [tab, counts.edited]);

  const toggleCluster = useCallback((id: MatchingClusterId) => {
    setOpenClusters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleTabChange = useCallback(
    (next: MatchingReportTab) => {
      setTab(next);
      const first = firstMatchingClusterIdForTab(next, allGroups);
      setOpenClusters(first ? new Set([first]) : new Set());
    },
    [allGroups]
  );

  const handleTabKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, current: MatchingReportTab) => {
      const currentIndex = visibleTabs.indexOf(current);
      let nextIndex: number | null = null;
      if (event.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % visibleTabs.length;
      } else if (event.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
      } else if (event.key === "Home") {
        nextIndex = 0;
      } else if (event.key === "End") {
        nextIndex = visibleTabs.length - 1;
      }
      if (nextIndex === null) return;
      event.preventDefault();
      const nextTab = visibleTabs[nextIndex];
      handleTabChange(nextTab);
      document.getElementById(`matching-report-tab-${nextTab}`)?.focus();
    },
    [handleTabChange, visibleTabs]
  );

  const renderChecksCell = (item: ClusteredMatchingRow) => {
    const isEdited = item.status === "edited";
    const isError = item.status === "error";
    const isWarning =
      !isEdited &&
      !isError &&
      (item.status === "warning" || item.warnings.length > 0);
    const badgeClass = isEdited
      ? styles.badgeEdited
      : isError
        ? styles.badgeFail
        : isWarning
          ? styles.badgeWarn
          : styles.badgePass;
    // Label must match chip colour — never yellow "Imported"/"Ready".
    const label = isWarning ? "Warning" : checkBadgeLabel(item.status);

    return (
      <div className={styles.checkInner}>
        <div className={styles.checkBadgeRow}>
          <span className={badgeClass} aria-label={label}>
            {label}
          </span>
        </div>
        {(isEdited ||
          item.errors.length > 0 ||
          item.warnings.length > 0) && (
          <ul className={styles.reasons}>
            {isEdited && (
              <li className={styles.reasonNote}>
                Re-validate to check this row again.
              </li>
            )}
            {!isEdited &&
              item.errors.map((msg) => (
                <li key={`err:${msg}`} className={styles.reason}>
                  {msg}
                </li>
              ))}
            {!isEdited &&
              item.warnings.map((msg) => {
                const match = (result?.warnings ?? []).find(
                  (w) => w.warning === msg && w.indices.includes(item.index)
                );
                const id = match
                  ? `${match.code}:${match.indices.join(",")}`
                  : `MATCH-WARN:${item.index}`;
                const skipLocked =
                  phase === "committed" &&
                  !!match &&
                  SKIP_ON_COMMIT_CODES.has(match.code);
                return (
                  <li key={`warn:${msg}`} className={styles.reasonWarning}>
                    {msg}{" "}
                    {!skipLocked && (
                      <button
                        type="button"
                        className={styles.dismiss}
                        onClick={() => onDismissWarning(id)}
                        disabled={dismissDisabled}
                      >
                        Dismiss
                      </button>
                    )}
                  </li>
                );
              })}
          </ul>
        )}
      </div>
    );
  };

  const renderSelectHeader = (visibleRows: ClusteredMatchingRow[]) => {
    if (!showSelectColumn || !onSetVisibleSelection) return null;
    const selectable = visibleRows.filter(isRowSelectable);
    const selectableIndices = selectable.map((r) => r.index);
    const selectedVisible = selectableIndices.filter((i) => selected.has(i));
    const allSelected =
      selectableIndices.length > 0 &&
      selectedVisible.length === selectableIndices.length;
    const someSelected = selectedVisible.length > 0 && !allSelected;
    return (
      <th
        scope="col"
        className={styles.selectColumn}
        aria-label="Select rows"
      >
        <Checkbox
          isSelected={allSelected}
          isIndeterminate={someSelected}
          isDisabled={selectionDisabled || selectableIndices.length === 0}
          onChange={(isSelected) =>
            onSetVisibleSelection(selectableIndices, isSelected)
          }
          aria-label={
            allSelected
              ? "Clear selection for visible rows"
              : someSelected
                ? "Select remaining visible rows"
                : "Select all visible rows"
          }
        />
      </th>
    );
  };

  const renderRow = (item: ClusteredMatchingRow) => {
    const dates =
      item.row.startDate && item.row.finishDate
        ? `${item.row.startDate}–${item.row.finishDate}`
        : "—";
    const selectable = isRowSelectable(item);
    return (
      <tr key={item.index} data-status={item.status}>
        {showSelectColumn && (
          <td className={styles.selectCell}>
            <Checkbox
              isSelected={selected.has(item.index)}
              isDisabled={selectionDisabled || !selectable}
              onChange={() => onToggleRow!(item.index)}
              aria-label={`Select row ${item.row.fileRow}${item.row.employeeCode ? ` (${item.row.employeeCode})` : ""}`}
            />
          </td>
        )}
        {showEditColumn && (
          <td
            className={`${styles.actionsCell}${showSelectColumn ? ` ${styles.actionsCellOffset}` : ""}`}
          >
            <Button
              variant="tertiary"
              onPress={() => onEditRow!(item.index)}
              isDisabled={editDisabled}
              aria-label={`Edit row ${item.row.fileRow}${item.row.employeeCode ? ` (${item.row.employeeCode})` : ""}`}
            >
              Edit
            </Button>
          </td>
        )}
        <td>{item.row.fileRow}</td>
        <td>{item.row.rawMatchAction || item.row.matchAction}</td>
        <td>{item.row.employeeCode}</td>
        <td>{item.row.componentCode ?? "—"}</td>
        <td>{item.row.componentName}</td>
        <td>{dates}</td>
        <td>
          {item.row.targetAmount === null ? "—" : item.row.targetAmount}
        </td>
        <td className={styles.checkCell}>{renderChecksCell(item)}</td>
      </tr>
    );
  };

  return (
    <section className={styles.report} aria-label="Matching validation report">
      {banner && (
        <div
          className={`${styles.banner} ${
            banner.kind === "error"
              ? styles.bannerError
              : banner.kind === "warn"
                ? styles.bannerWarn
                : styles.bannerSuccess
          }`}
          role={banner.kind === "error" ? "alert" : "status"}
        >
          {banner.text}
        </div>
      )}

      <div
        className={styles.tabs}
        role="tablist"
        aria-label="Filter validation report by status"
      >
        {visibleTabs.map((key) => {
          const selected = tab === key;
          return (
            <button
              key={key}
              id={`matching-report-tab-${key}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`matching-report-panel-${key}`}
              tabIndex={selected ? 0 : -1}
              className={`${styles.tab} ${selected ? styles.tabActive : ""}`}
              onClick={() => handleTabChange(key)}
              onKeyDown={(event) => handleTabKeyDown(event, key)}
            >
              {TAB_LABELS[key]} ({counts[key]})
            </button>
          );
        })}
      </div>

      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles.nameSearch}
          placeholder="Search employee, component, MatchAction, dates, amount, pay run…"
          aria-label="Search matching rows"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div
          className={styles.layoutToggle}
          role="group"
          aria-label="Report layout"
        >
          <button
            type="button"
            className={`${styles.layoutButton} ${layoutMode === "byIssue" ? styles.layoutButtonActive : ""}`}
            aria-pressed={layoutMode === "byIssue"}
            onClick={() => setLayoutMode("byIssue")}
          >
            By issue
          </button>
          <button
            type="button"
            className={`${styles.layoutButton} ${layoutMode === "flat" ? styles.layoutButtonActive : ""}`}
            aria-pressed={layoutMode === "flat"}
            onClick={() => setLayoutMode("flat")}
          >
            All rows
          </button>
        </div>
      </div>

      <div
        id={`matching-report-panel-${tab}`}
        role="tabpanel"
        aria-labelledby={`matching-report-tab-${tab}`}
        className={styles.clusters}
      >
        {layoutMode === "flat" ? (
          filteredFlatRows.length === 0 ? (
            <p className={styles.emptyCluster}>No rows match this filter.</p>
          ) : (
            <div className={styles.tableWrap} data-testid="matching-flat-table">
              <table className={styles.table}>
                <thead>
                  <tr>
                    {renderSelectHeader(filteredFlatRows)}
                    {showEditColumn && (
                      <th
                        scope="col"
                        className={`${styles.actionsColumn}${showSelectColumn ? ` ${styles.actionsColumnOffset}` : ""}`}
                        aria-label="Actions"
                      />
                    )}
                    <th scope="col">Row #</th>
                    <th scope="col">MatchAction</th>
                    <th scope="col">EmployeeCode</th>
                    <th scope="col">ComponentCode</th>
                    <th scope="col">ComponentName</th>
                    <th scope="col">Dates</th>
                    <th scope="col">TargetAmount</th>
                    <th scope="col">Checks</th>
                  </tr>
                </thead>
                <tbody>{filteredFlatRows.map(renderRow)}</tbody>
              </table>
            </div>
          )
        ) : filteredGroups.length === 0 ? (
          <p className={styles.emptyCluster}>No rows match this filter.</p>
        ) : (
          filteredGroups.map((group) => {
            const isOpen = openClusters.has(group.clusterId);
            const chipClass =
              group.config.kind === "error"
                ? styles.countChip_error
                : group.config.kind === "warning"
                  ? styles.countChip_warning
                  : group.config.kind === "edited"
                    ? styles.countChip_edited
                    : styles.countChip_passed;
            return (
              <article
                key={group.clusterId}
                className={styles.cluster}
                data-testid={`matching-cluster-${group.clusterId}`}
              >
                <button
                  type="button"
                  className={styles.clusterHeader}
                  aria-expanded={isOpen}
                  aria-label={`${group.config.title}, ${group.rows.length} rows, ${isOpen ? "expanded" : "collapsed"}`}
                  onClick={() => toggleCluster(group.clusterId)}
                >
                  <div className={styles.clusterTitleGroup}>
                    <span className={styles.clusterTitle}>
                      {group.config.title}
                    </span>
                    {group.config.hint ? (
                      <span className={styles.clusterHint}>
                        {group.config.hint}
                      </span>
                    ) : null}
                  </div>
                  <span className={`${styles.countChip} ${chipClass}`}>
                    {group.rows.length}
                  </span>
                </button>
                {isOpen && (
                  <div className={styles.clusterBody}>
                    <div className={styles.tableWrap}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            {renderSelectHeader(group.rows)}
                            {showEditColumn && (
                              <th
                                scope="col"
                                className={`${styles.actionsColumn}${showSelectColumn ? ` ${styles.actionsColumnOffset}` : ""}`}
                                aria-label="Actions"
                              >
                                {/* Intentionally empty — keeps the sticky Edit column narrow */}
                              </th>
                            )}
                            <th scope="col">Row #</th>
                            <th scope="col">MatchAction</th>
                            <th scope="col">EmployeeCode</th>
                            <th scope="col">ComponentCode</th>
                            <th scope="col">ComponentName</th>
                            <th scope="col">Dates</th>
                            <th scope="col">TargetAmount</th>
                            <th scope="col">Checks</th>
                          </tr>
                        </thead>
                        <tbody>{group.rows.map(renderRow)}</tbody>
                      </table>
                    </div>
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>
    </section>
  );
}
